import {
  BodyText,
  Button,
  Callout,
  Combobox,
  DatePicker,
  Skeleton,
  toast,
} from "@/components/ui";
import { useEpisodesOfCareList } from "@/network/useEpisodesOfCare";
import { useParams } from "@tanstack/react-router";
import {
  format,
  formatDistanceToNow,
  isBefore,
  isSameDay,
  startOfDay,
} from "date-fns";
import { RotateCcw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useTimelineEncounters } from "./network/useTimelineEncounters";

import type { ComboboxOption } from "@/components/ui/Combobox";
import { TRACKERS } from "@/constants/Trackers";
import { capitalize } from "@/lib/formatters";
import { formatDateUTC } from "@/lib/time";
import { trackEvent } from "@/lib/tracking";
import { SectionHeader } from "../components/SectionHeader";
import { EncounterCard } from "./EncounterCard";

export const Timeline = () => {
  const { patientId } = useParams({ from: "/patient/$patientId" });

  const [selectedOptions, setSelectedOptions] = useState<ComboboxOption[]>([]);
  const [date, setDate] = useState(new Date());
  const [showScrollAction, setShowScrollAction] = useState(false);

  const timelineEncountersRef = useRef<HTMLDivElement>(null);
  const { ref, inView } = useInView();

  const {
    data: eocData,
    isLoading: isLoadingEOCs,
    isError: isEocsError,
  } = useEpisodesOfCareList({ patientId, includeEncounterDiagnoses: true });

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading: isLoadingEncounters,
    isError: isEncountersError,
  } = useTimelineEncounters({
    patientId,
    notAfter: formatDateUTC(date), // Format date in UTC timezone, required by charting API
    eocs: selectedOptions.reduce((acc, curr) => `${acc},${curr.value}`, ""),
  });

  const isLoading = isLoadingEncounters || isLoadingEOCs;
  const isNoResults =
    !data?.pages.length || !data?.pages[0].encountersByDate.length;

  const showResetButton =
    !!selectedOptions.length ||
    isBefore(new Date(date), startOfDay(new Date()));

  const options = useMemo(
    () =>
      eocData?.map(({ id, title }) => ({
        value: id,
        label: capitalize(title),
      })) || [],
    [eocData],
  );

  const handleClickReset = () => {
    setDate(new Date());
    setSelectedOptions([]);
  };

  const renderDateSubText = (date: string) => {
    const now = new Date();
    // Parse the date as noon local time to avoid timezone issues
    const dateAtNoon = new Date(`${date}T12:00:00`);
    if (isSameDay(dateAtNoon, now)) return "Today";
    return formatDistanceToNow(dateAtNoon, { addSuffix: true });
  };

  useEffect(() => {
    if (isEocsError) {
      toast.error("Failed to fetch patient's episodes of care", {
        id: "patient-timeline-eocs-error",
      });
    }
  }, [isEocsError]);

  useEffect(() => {
    if (inView) fetchNextPage();
  }, [fetchNextPage, inView]);

  return (
    <div className="w-full flex flex-col">
      <SectionHeader title="Timeline" showSeparator={true} />
      <div className="w-full flex items-center gap-2 pt-2 py-4">
        <Combobox
          options={options}
          itemText="Diagnoses"
          selectedOptions={selectedOptions}
          onChange={(value) => {
            trackEvent(TRACKERS.FILTER_TIMELINE_EOC);
            setSelectedOptions(value);
          }}
        />
        <DatePicker
          date={date}
          setDate={setDate}
          label=""
          closeOnSelect
          disableFuture
        />
        {showResetButton && (
          <button
            type="button"
            onClick={handleClickReset}
            data-testid="reset-timeline-filters"
          >
            <RotateCcw className="text-success" size={14} />
          </button>
        )}
      </div>

      <div
        className="overflow-auto pb-2"
        ref={timelineEncountersRef}
        onScroll={(e) =>
          setShowScrollAction((e.target as HTMLDivElement).scrollTop > 140)
        }
      >
        {showScrollAction && (
          <Button
            size="sm"
            onClick={() =>
              timelineEncountersRef.current?.scroll({
                top: 0,
                behavior: "smooth",
              })
            }
          >
            Jump to Top
          </Button>
        )}

        {isEncountersError && (
          <Callout
            title="Something went wrong"
            description="Unable to load encounters."
            variant="danger"
            className="timeline-error"
          />
        )}

        {isNoResults && !isLoading && !isEncountersError && (
          <BodyText tag="p" size="sm" textColor="tertiary">
            No encounters documented
          </BodyText>
        )}

        {data?.pages.map((data) =>
          data?.encountersByDate.map(({ date, encounters }) => (
            <div key={`encounter-${date}`}>
              <div className="flex justify-between mt-3 mb-2">
                <BodyText tag="p" size="sm" bold>
                  {format(new Date(`${date}T12:00:00`), "MM/dd/yyyy")}
                </BodyText>

                <BodyText tag="p" textColor="tertiary" size="xs">
                  {renderDateSubText(date)}
                </BodyText>
              </div>
              <div className="flex flex-col gap-2">
                {encounters?.map((encounter) => (
                  <EncounterCard key={encounter.id} encounter={encounter} />
                ))}
              </div>
            </div>
          )),
        )}

        {(isFetching || hasNextPage) && (
          <div
            className="flex flex-col gap-2 mt-3"
            data-testid="timeline-skeleton"
            {...(hasNextPage && { ref })}
          >
            {Array.from({ length: 4 }).map((_, i) => (
              //  biome-ignore lint/suspicious/noArrayIndexKey: unneeded for loading skeleton
              <div key={i} className="flex flex-col gap-2">
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-16" />
                </div>
                {Array.from({ length: 3 }).map((_, i) => (
                  //  biome-ignore lint/suspicious/noArrayIndexKey: unneeded for loading skeleton
                  <Skeleton className="w-full h-[55px]" key={i} />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
